import { useState } from 'react';
import {
  Badge,
  Button,
  ButtonIcon,
  Flex,
  SearchField,
  Text,
  Tooltip,
  TooltipTrigger,
} from '@backstage/ui';
import { RiAddLine, RiDeleteBinLine, RiEditLine } from '@remixicon/react';
import { TrackedTopic, ItemStatus } from '../types';
import { TopicDialog } from './TopicDialog';
import styles from '../ServiceBusTopicManager.module.css';

interface TopicListProps {
  topics: TrackedTopic[];
  onAdd: (name: string) => void;
  onEdit: (id: string, newName: string) => void;
  onDelete: (id: string) => void;
  onRestore: (id: string) => void;
  selectedTopic: string | null;
  onSelectTopic: (name: string | null) => void;
}

const statusLabel: Record<ItemStatus, string> = {
  original: '',
  added: 'novo',
  modified: 'editado',
  deleted: 'removido',
};

export const TopicList = ({
  topics,
  onAdd,
  onEdit,
  onDelete,
  onRestore,
  selectedTopic,
  onSelectTopic,
}: TopicListProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTopic, setEditingTopic] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [filter, setFilter] = useState('');

  const activeTopics = topics.filter(t => t.status !== 'deleted');
  const existingNames = activeTopics.map(t => t.name);
  const visibleTopics = filter
    ? topics.filter(t => t.name.toLowerCase().includes(filter.toLowerCase()))
    : topics;

  const handleOpenCreate = () => {
    setEditingTopic(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (id: string, name: string) => {
    setEditingTopic({ id, name });
    setDialogOpen(true);
  };

  const handleSave = (name: string) => {
    if (editingTopic) {
      onEdit(editingTopic.id, name);
    } else {
      onAdd(name);
    }
  };

  return (
    <Flex direction="column">
      <Flex align="center" justify="between">
        <Text variant="title-small">Tópicos ({activeTopics.length})</Text>
        <Button variant="secondary" onClick={handleOpenCreate}>
          Novo Tópico
        </Button>
      </Flex>

      {topics.length > 0 && (
        <SearchField
          placeholder="Buscar tópico..."
          value={filter}
          onChange={setFilter}
        />
      )}

      {topics.length === 0 || visibleTopics.length === 0 ? (
        <div className={styles.emptyState}>
          <Text variant="body-small">
            {topics.length === 0
              ? 'Nenhum tópico encontrado. Clique em "Novo Tópico" para adicionar.'
              : `Nenhum tópico encontrado para "${filter}".`}
          </Text>
        </div>
      ) : (
        <div className={styles.list}>
          {visibleTopics.map(topic => (
            <button
              type="button"
              key={topic.id}
              className={`${styles.listItem} ${
                topic.status === 'deleted' ? styles.deletedItem : ''
              }`}
              onClick={() => {
                if (topic.status !== 'deleted') {
                  onSelectTopic(
                    selectedTopic === topic.name ? null : topic.name,
                  );
                }
              }}
            >
              <Flex align="center">
                <Text variant="body-small">{topic.name}</Text>
                {topic.status !== 'original' && (
                  <Badge size="small">{statusLabel[topic.status]}</Badge>
                )}
              </Flex>
              <Flex>
                {topic.status === 'deleted' ? (
                  <TooltipTrigger>
                    <ButtonIcon
                      aria-label="Restaurar"
                      variant="secondary"
                      icon={<RiAddLine size={16} />}
                      onPress={() => onRestore(topic.id)}
                    />
                    <Tooltip>Restaurar</Tooltip>
                  </TooltipTrigger>
                ) : (
                  <>
                    <TooltipTrigger>
                      <ButtonIcon
                        aria-label="Editar"
                        variant="secondary"
                        icon={<RiEditLine size={16} />}
                        onPress={() => handleOpenEdit(topic.id, topic.name)}
                      />
                      <Tooltip>Editar</Tooltip>
                    </TooltipTrigger>
                    <TooltipTrigger>
                      <ButtonIcon
                        aria-label="Deletar"
                        variant="secondary"
                        icon={<RiDeleteBinLine size={16} />}
                        onPress={() => onDelete(topic.id)}
                      />
                      <Tooltip>Deletar</Tooltip>
                    </TooltipTrigger>
                  </>
                )}
              </Flex>
            </button>
          ))}
        </div>
      )}

      <TopicDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
        initialName={editingTopic?.name || ''}
        existingNames={existingNames}
        mode={editingTopic ? 'edit' : 'create'}
      />
    </Flex>
  );
};
